/**
 * Header-gedrag: transparant over de hero, het mobiele menu, en de taalwissel
 * die meebeweegt met de scrollrichting.
 *
 * De transparante staat geldt alleen als de sectie hem meegeeft (data-overlay),
 * en vervalt zodra je voorbij de drempel scrollt of het menu opent. Precies de
 * drie voorwaarden uit de Next-versie: overHero && !scrolled && !mobileOpen.
 *
 * De scrollluisteraar hangt er op elke pagina, niet alleen over de hero: de
 * taalwissel moet ook op de contact- en productpagina meebewegen.
 */

// Onder deze afstand tellen we een scroll niet als richtingswissel. Zonder
// zo'n marge klapt de taalwissel heen en weer bij het kleinste trillen.
const RICHTING_MARGE = 4;

class FoleaHeader extends HTMLElement {
  connectedCallback() {
    this.overlay = this.dataset.overlay === "true";
    this.threshold = Number(this.dataset.threshold ?? 40);
    this.menuOpen = false;
    this.ticking = false;
    this.lastY = window.scrollY;

    this.toggle = this.querySelector("[data-menu-toggle]");
    this.nav = this.querySelector("[data-mobile-nav]");
    this.iconClosed = this.querySelector('[data-menu-icon="closed"]');
    this.iconOpen = this.querySelector('[data-menu-icon="open"]');

    if (this.toggle && this.nav) {
      this.onToggle = this.onToggle.bind(this);
      this.toggle.addEventListener("click", this.onToggle);
    }

    this.onScroll = this.onScroll.bind(this);
    window.addEventListener("scroll", this.onScroll, { passive: true });
    this.applyScrollState();

    // Een sectie die opnieuw rendert in de theme editor laat het menu open
    // staan; sluit het dan, zodat de staat klopt met wat je ziet.
    this.closeMenu();
  }

  disconnectedCallback() {
    window.removeEventListener("scroll", this.onScroll);
    if (this.toggle) this.toggle.removeEventListener("click", this.onToggle);
  }

  onScroll() {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      this.applyScrollState();
      this.ticking = false;
    });
  }

  applyScrollState() {
    const y = window.scrollY;
    const scrolled = y > this.threshold;

    if (this.overlay && !scrolled && !this.menuOpen) {
      this.dataset.transparent = "true";
    } else {
      delete this.dataset.transparent;
    }

    const omlaag = y > this.lastY + RICHTING_MARGE;
    const omhoog = y < this.lastY - RICHTING_MARGE;

    // Bovenaan de pagina en met het menu open staat hij er altijd. Daarbuiten
    // bepaalt de richting het: weg terwijl je leest, terug zodra je omhoog
    // gaat, zodat je er niet voor naar de top hoeft.
    if (!scrolled || this.menuOpen || omhoog) {
      delete this.dataset.languageHidden;
    } else if (omlaag) {
      this.dataset.languageHidden = "true";
    }

    // Alleen bijwerken als de marge gehaald is, anders telt een trage scroll
    // nooit als richting.
    if (omlaag || omhoog) this.lastY = y;
  }

  onToggle() {
    this.menuOpen ? this.closeMenu() : this.openMenu();
  }

  openMenu() {
    this.menuOpen = true;
    this.nav.dataset.open = "true";
    this.toggle.setAttribute("aria-expanded", "true");
    this.iconClosed?.classList.add("hidden");
    this.iconOpen?.classList.remove("hidden");
    this.applyScrollState();
  }

  closeMenu() {
    this.menuOpen = false;
    if (this.nav) delete this.nav.dataset.open;
    this.toggle?.setAttribute("aria-expanded", "false");
    this.iconClosed?.classList.remove("hidden");
    this.iconOpen?.classList.add("hidden");
    this.applyScrollState();
  }
}

if (!customElements.get("folea-header")) {
  customElements.define("folea-header", FoleaHeader);
}
